import type { ImageMode } from '@lyrmo/shared';
import { useEffect, useLayoutEffect, useRef, type RefObject } from 'react';
import { Icon } from '../../components/Icon';
import { useI18n } from '../../i18n/i18n-context';
import { FileName } from './FileName';
import { growFromButton } from './image-progress';
import './image-progress.css';

/**
 * A small picture of an image that came in, drawn from the file itself: the image icon until it
 * shows, and in its place if the browser can't show it.
 */
export function ImageThumb({ file }: { readonly file: File }) {
  const imageRef = useRef<HTMLImageElement>(null);
  useEffect(() => {
    const image = imageRef.current;
    if (!image || typeof URL.createObjectURL !== 'function') return;
    const url = URL.createObjectURL(file);
    image.src = url;
    return () => {
      image.removeAttribute('src');
      delete image.dataset.shown;
      URL.revokeObjectURL(url);
    };
  }, [file]);
  return (
    <span className="image-thumb" aria-hidden="true">
      <Icon name="image" />
      <img
        ref={imageRef}
        alt=""
        onLoad={(event) => {
          event.currentTarget.dataset.shown = '';
        }}
      />
    </span>
  );
}

interface ImageProgressProps {
  readonly mode: ImageMode;
  readonly file: File;
  readonly fileName: string;
  /** Where the choice pressed was, for the progress to grow out of it. */
  readonly from: DOMRect | null;
  /** The progress itself, for it to close to a spark once the work is done (image-progress.ts). */
  readonly pillRef: RefObject<HTMLDivElement | null>;
  readonly onCancel: () => void;
}

/**
 * An image being read or described: the choice pressed grows across the bar, a light running to
 * and fro in it while the work goes on, with the image, what's being done, and its name in it.
 * Cancel keeps its place.
 */
export function ImageProgress({
  mode,
  file,
  fileName,
  from,
  pillRef,
  onCancel,
}: ImageProgressProps) {
  const { t } = useI18n();
  useLayoutEffect(() => {
    growFromButton(pillRef.current, from);
  }, [from, pillRef]);
  return (
    <div className="editor-import-status image-progress-bar">
      <div className="image-progress-track">
        <div ref={pillRef} className="image-progress" data-mode={mode}>
          <span className="image-progress-glow" aria-hidden="true">
            <span className="image-progress-scan" />
          </span>
          <p className="image-progress-content" role="status">
            <ImageThumb file={file} />
            <Icon name={mode === 'text' ? 'read-text' : 'describe-image'} />
            <span className="image-progress-label">
              {mode === 'text' ? t.editor.imageTextProgress : t.editor.describingImage}
            </span>
            <FileName names={[fileName]} />
          </p>
        </div>
      </div>
      <button
        type="button"
        className="button button--ghost icon-button"
        aria-label={t.common.cancel}
        title={t.common.cancel}
        onClick={onCancel}
      >
        <Icon name="close" />
      </button>
    </div>
  );
}
